import { CloseButton, SettingsButton, SaveButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import { IComparisonPopulationSetting } from '../../../../data/providers/settings/comparisonPopulationSettingsProvider';
import {
  DateRangeTypeOption,
  IDateRange,
  IExclusiveDateOption,
} from '../../../../data/providers/settings/dates/dateRangeProvider';
import {
  IProfileReportSettings,
  IProfileReportSettingsState,
  profileReportSettingsProvider,
} from '../../../../data/providers/settings/reports/profileReportSettingsProvider';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import { isValidComparisonPopulation } from '../../../../data/providers/utils/isValidComparisonPopulation';
import { isValidDateRange } from '../../../../data/providers/utils/isValidDateRange';
import {
  EZScorePreference,
  IRange,
  ISelectOption,
} from '../../../../data/types';
import {
  deleteSelectedTemplate,
  saveProfileSettings,
} from '../../../../data/writers/settings/reports/profileReportSettings';
import { Row } from '../../../layout/Row';
import { ComparisonSettings } from '../../../settings/comparison/ComparisonSettings';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { Select } from '../../../shared/form/Select';
import { DateSelect } from '../../../shared/form/selects/DateRangeSelect';
import { Step } from '../../../shared/form/Step';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { FormSection } from '../../utils/settings/FormSection';
import { LeftColumn } from '../../utils/settings/LeftColumn';
import { RightColumn } from '../../utils/settings/RightColumn';
import { TemplateSelect } from '../../utils/settings/TemplateSelect';
import { TemplateForm } from './TemplateForm';

const graphTypeOptions = [
  { label: 'Radar', value: 'radar' },
  { label: 'Bar (Sessions)', value: 'bar' },
];
interface IPopupProps {
  onClose: () => void;
  onSave: () => void;
}

export const Popup = (props: IPopupProps): React.ReactElement => {
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<ISettingsTemplate<IProfileReportSettings>>();
  const [templates, setTemplates] = React.useState<
    Array<ISettingsTemplate<IProfileReportSettings>>
  >([]);
  const [initialTemplate, setInitialTemplate] =
    React.useState<ISettingsTemplate<IProfileReportSettings>>();
  const [targetTimeframe, setTargetTimeframe] = React.useState<IDateRange>();
  const [comparisonPopulation, setComparisonPopulation] =
    React.useState<IComparisonPopulationSetting>();
  const [viewType, setViewType] = React.useState<ISelectOption<string>>();

  const [saving, setSaving] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);

  /**
   * @description subscribe to trend report settings
   */
  React.useEffect((): (() => void) => {
    const subscriber = profileReportSettingsProvider().subscribe(
      (mSettings: IProfileReportSettingsState): void => {
        setTemplates(mSettings?.templates);
        setSelectedTemplate(mSettings?.selected);
        setInitialTemplate(mSettings?.selected);
        /// do not override local state if there are no values
        if (!isEmpty(mSettings?.comparisonPopulation)) {
          setComparisonPopulation(mSettings?.comparisonPopulation);
        }
        if (!isEmpty(mSettings?.targetTimeframe)) {
          setTargetTimeframe(mSettings?.targetTimeframe);
        }
        if (!isEmpty(mSettings?.graph)) {
          setViewType(mSettings?.graph);
        }
        setLoading(false);
      }
    );

    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description handles change to the selected template
   * @param mSelectedTemplate
   */
  const templateChangeHandler = (
    mSelectedTemplate: ISettingsTemplate<IProfileReportSettings>
  ): void => {
    setSelectedTemplate(mSelectedTemplate);
  };

  /**
   * @description saves the change to database
   */
  const handleSave = async (): Promise<void> => {
    setSaving(true);
    if (!isEqual(initialTemplate?.id, selectedTemplate?.id)) {
      props.onSave();
    }
    await saveProfileSettings(
      selectedTemplate,
      targetTimeframe,
      viewType,
      comparisonPopulation
    );
    setSaving(false);
    props.onClose();
  };

  /**
   * @description handles the change to the view type
   * @param range
   */
  const graphTypeHandler = (graph: ISelectOption<string>): void => {
    setViewType(graph);
  };

  /**
   * @description handle change to date range
   * @param dateType
   * @param dateRange
   */
  const dateRangeChangeHandler = (
    dateType: DateRangeTypeOption,
    dateRange: IRange
  ): void => {
    setTargetTimeframe({
      dateType,
      dateRange,
    });
  };

  /**
   * @description handles change to the comparsion population entity
   * @param entity
   */
  const handleComparisonEntityChange = (entity: ISelectOption): void => {
    setComparisonPopulation({
      ...comparisonPopulation,
      entity,
    });
  };

  /**
   * @description handles date change
   */

  const handleComparisonDateChange = (
    dateRangeType: IExclusiveDateOption,
    dateRangeValues: IRange
  ): void => {
    setComparisonPopulation({
      ...comparisonPopulation,
      dateRangeType,
      dateRangeValues,
    });
  };

  /**
   * @description handle change to comparison zscore preference
   * @param pref
   */
  const handleComparisonZScorePreferenceChange = (
    pref: EZScorePreference
  ): void => {
    setComparisonPopulation({
      ...comparisonPopulation,
      zScorePreference: pref,
    });
  };

  /**
   * @description deletes the supplied template and finds a suitable replacement for the selected template
   * @param template
   */
  const templateDeleteHandler = async (
    template: ISettingsTemplate<IProfileReportSettings>
  ): Promise<void> => {
    const index = templates.findIndex(
      (tmp: ISettingsTemplate<IProfileReportSettings>): boolean =>
        tmp.id === template.id
    );
    /// get either the template before or after the template being deleted
    const replacement: ISettingsTemplate<IProfileReportSettings> =
      index === 0 ? templates?.[1] : templates?.[index - 1];

    await deleteSelectedTemplate(template, replacement);
  };

  /**
   * @description determines if the setting is valid and is able to be saved
   */
  const isValid = React.useMemo((): boolean => {
    /// make sure the user has selected a valid timeframe
    if (!isValidDateRange(targetTimeframe)) return false;
    /// make sure the user has selected a template
    if (isEmpty(selectedTemplate)) return false;
    /// make sure the user has selected a view type
    if (isEmpty(viewType)) return false;

    if (!isValidComparisonPopulation(comparisonPopulation)) return false;
    /// form is valid and can be submitted
    return true;
  }, [
    JSON.stringify(targetTimeframe),
    JSON.stringify(viewType),
    JSON.stringify(selectedTemplate),
    JSON.stringify(comparisonPopulation),
  ]);

  return (
    <PopupCard onClose={props.onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>Profile Report Settings</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {loading || (saving && <LinearLoading />)}

          <Row shouldCollapse style={{ height: '100%' }}>
            <LeftColumn>
              <TemplateSelect
                step={1}
                templates={templates}
                onSelect={templateChangeHandler}
                onDelete={templateDeleteHandler}
                selectedTemplateId={selectedTemplate?.id}
                formComponent={TemplateForm}
                reportType={'Profile Report'}
                reportTypeId='profileReport'
              />
            </LeftColumn>
            <RightColumn>
              <Row style={{ flex: 0, alignItems: 'center' }}>
                <FormSection style={{ flex: 1 }}>
                  <CardHeader>
                    <CardHeaderLeft>
                      <Step>2</Step>
                      <h6>Select Timeframe</h6>
                    </CardHeaderLeft>
                  </CardHeader>
                  <ControlSpacer style={{ padding: '10px' }}>
                    <DateSelect
                      type='inclusive'
                      dateRangeType={targetTimeframe?.dateType}
                      dateRange={targetTimeframe?.dateRange}
                      onChange={dateRangeChangeHandler}
                    />
                  </ControlSpacer>
                </FormSection>
                <FormSection style={{ flex: 1 }}>
                  <CardHeader>
                    <CardHeaderLeft>
                      <Step>3</Step>
                      <h6>Select View Type</h6>
                    </CardHeaderLeft>
                  </CardHeader>
                  <ControlSpacer style={{ padding: '10px' }}>
                    <ControlLabel style={{ marginTop: '10px' }}>
                      View Type
                    </ControlLabel>
                    <Select
                      options={graphTypeOptions}
                      value={viewType}
                      controlShouldRenderValue
                      placeholder='Graph Type...'
                      onChange={graphTypeHandler}
                      cache={false}
                      id={'input-select'}
                    />
                  </ControlSpacer>
                </FormSection>
              </Row>

              <FormSection>
                <CardHeader>
                  <CardHeaderLeft>
                    <Step>4</Step>
                    <h6>Select Comparison Population</h6>
                  </CardHeaderLeft>
                </CardHeader>
                <ComparisonSettings
                  handleEntityChange={handleComparisonEntityChange}
                  handleZScorePreferenceChange={
                    handleComparisonZScorePreferenceChange
                  }
                  handleDateChange={handleComparisonDateChange}
                  settings={comparisonPopulation}
                />
              </FormSection>
            </RightColumn>
          </Row>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton onClick={handleSave} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface IProps extends RouteComponentProps {
  style?: React.CSSProperties;
  onSave: () => void;
}
export const ProfileSettings = withRouter(
  (props: IProps): React.ReactElement => {
    const open = (): void =>
      openPopup(
        <Popup onClose={closePopup(props.history)} onSave={props.onSave} />,
        props.history,
        'profile-report-settings'
      );

    return <SettingsButton onClick={open} />;
  }
);
